import { useState } from 'react';
import SVG from 'react-inlinesvg';
import cx from 'classnames';
import { copyToClipboard } from 'utils/copyToClipboard';
import copyIcon from 'assets/icons/copy.svg';
import checkIcon from 'assets/icons/check.svg';

export interface CopyToClipboardCellRendererProps {
  value: string;
  displayValue?: string;
  colorClasses?: string;
  isIconAlwaysVisible?: boolean;
  customClasses?: string;
  customIconClasses?: string;
}

const CopyToClipboardCellRenderer = ({
  value,
  displayValue,
  colorClasses = 'hover:bg-primary-300',
  isIconAlwaysVisible,
  customClasses,
  customIconClasses,
}: CopyToClipboardCellRendererProps): React.ReactElement | null => {
  const [isCopied, setIsCopied] = useState(false);

  if (!value) return null;

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    copyToClipboard(value);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1000);
  };

  return (
    <button
      type="button"
      onClick={handleOnClick}
      className={cx(
        'group/copy p-1 border border-transparent hover:rounded max-w-full flex items-center',
        colorClasses,
        customClasses,
      )}
    >
      <p className="min-w-0 truncate">{displayValue ?? value}</p>
      <div
        className={cx('flex items-start w-4 h-4 ml-2 shrink-0', {
          'invisible group-hover/copy:visible': !isIconAlwaysVisible,
        })}
      >
        {isCopied ? (
          <p data-test-id="checkmark">
            <SVG src={checkIcon} className={cx('w-4 h-4', customIconClasses)} />
          </p>
        ) : (
          <p data-test-id="copy-icon">
            <SVG src={copyIcon} className={cx('w-4 h-4', customIconClasses)} />
          </p>
        )}
      </div>
    </button>
  );
};

export default CopyToClipboardCellRenderer;
